import { eventEmitter } from './eventEmitter/index.js';
import { httpClient } from "@/services/HttpClient";
import { profileStore } from "@/stores/profile";
class Player {
    /** @type {'none' | 'all' | 'current'} */
    repeat = 'none';
    constructor() {
        this.#player = document.createElement('audio');
        this.#player.addEventListener('playing', () => {
            this.#updateMediaData();
        });
    }
    addEventListener(...args) {
        return this.#player.addEventListener(...args);
    }
    get currentTime() {
        return this.#player.currentTime;
    }
    set currentTime(time) {
        this.#player.currentTime = time;
        this.#savePlayerStatus();
        eventEmitter.emit('current time change', { data: { currentTime: time } });
    }
    set currentTrack(currentTrack) {
        this.#stopSendingTrackStatus();
        if (this.#currentTrack?.id !== currentTrack?.id) {
            this.#player.currentTime = 0;
            this.#currentTrack = currentTrack;
            eventEmitter.emit('current track change', { data: { currentTrack } });
        }
    }
    get currentTrack() {
        return this.#currentTrack;
    }
    get currentTrackIndex() {
        return this.playlist.items.findIndex(i => i.id === this.currentTrack.id);
    }
    get duration() {
        return this.#currentTrack?.duration;
    }
    loadTrackStatus() {
        return httpClient.fetch('/api/v1/tracks/current/status')
            .then(({ response, body }) => {
            if (this.currentTrack)
                return;
            if (!response.ok) {
                return;
            }
            const { track, playlist, currentTime } = body;
            const s = currentTime.split(':').reduce((acc, time) => acc * 60 + +time, 0);
            this.#player.src = track.src;
            this.playlist = playlist;
            this.currentTrack = track;
            this.currentTime = s;
            this.#player.load();
        });
    }
    next() {
        this.currentTime = 0;
        const track = this.nextTrack || this.playlist.items[0];
        this.play(track);
        eventEmitter.emit('next');
    }
    get nextTrack() {
        if (this.currentTrackIndex === this.playlist.items.length - 1)
            return null;
        return this.playlist.items[this.currentTrackIndex + 1];
    }
    pause() {
        this.#player.pause();
        this.#player.removeEventListener('ended', this.#handleTrackPlayEnd);
        eventEmitter.emit('pause', { data: { currentTrack: this.#currentTrack } });
        this.#savePlayerStatus();
        this.#stopSendingTrackStatus();
    }
    /**
     * @param {Track | undefined} track
     * @param {Playlist | undefined} playlist
     * */
    play(track, playlist) {
        if (playlist)
            this.playlist = playlist;
        if (track && this.currentTrack?.id !== track.id) {
            this.#player.src = track.src;
            this.currentTrack = track;
        }
        this.#player.play();
        if (!this.#areActionHandlersInitialized) {
            this.#initActionHandlers();
        }
        this.#player.addEventListener('ended', this.#handleTrackPlayEnd, { once: true });
        this.#startSendingTrackStatus();
        this.#savePlayerStatus();
    }
    get playing() {
        return !this.#player.paused;
    }
    /** @param {Playlist} playlist  */
    set playlist(playlist) {
        eventEmitter.emit('playlist change', { data: { playlist, oldPlaylist: this.#playlist } });
        this.#playlist = playlist;
    }
    get playlist() {
        return this.#playlist;
    }
    previous() {
        eventEmitter.emit('previous');
        /** Если больше двух секунд, то рестарт */
        if (this.currentTime > 2) {
            this.currentTime = 0;
            return;
        }
        const track = this.previousTrack || this.playlist.items.at(-1);
        this.play(track);
    }
    get previousTrack() {
        if (this.currentTrackIndex === 0)
            return null;
        return this.playlist.items[this.currentTrackIndex - 1];
    }
    removeEventListener(...args) {
        return this.#player.removeEventListener(...args);
    }
    reset() {
        this.stop();
        this.repeat = 'none';
        this.currentTrack = null;
        this.playlist = [];
    }
    stop() {
        this.#player.pause();
        this.#player.removeEventListener('ended', this.#handleTrackPlayEnd);
        eventEmitter.emit('stop', { data: { currentTrack: this.#currentTrack } });
        this.#player.currentTime = 0;
        this.#stopSendingTrackStatus();
        this.#savePlayerStatus();
    }
    toggleRepeat() {
        const previousState = player.repeat;
        if (player.repeat === 'none') {
            player.repeat = 'all';
        }
        else if (player.repeat === 'all') {
            player.repeat = 'current';
        }
        else if (player.repeat === 'current') {
            player.repeat = 'none';
        }
        eventEmitter.emit('repeat change', { data: { previousState } });
    }
    #areActionHandlersInitialized = false;
    /** @type {Track | undefined} */
    #currentTrack;
    #handleTrackPlayEnd = () => {
        this.stop();
        if (this.repeat === 'current') {
            this.play();
        }
        else if (this.repeat === 'all') {
            this.next();
        }
        else {
            if (!this.nextTrack) {
                this.currentTrack = this.playlist.items[0];
            }
            else {
                this.next();
            }
        }
    };
    /** @type {HTMLAudioElement} */
    #player;
    /** @type {Playlist | null} */
    #playlist = null;
    /** @param {number | null} */
    #trackSendingIntervalId = null;
    #initActionHandlers() {
        navigator.mediaSession.setActionHandler('play', () => {
            this.play();
        });
        navigator.mediaSession.setActionHandler('previoustrack', () => {
            this.previous();
        });
        navigator.mediaSession.setActionHandler('nexttrack', () => {
            this.next();
        });
        navigator.mediaSession.setActionHandler('pause', () => {
            this.pause();
        });
        this.#areActionHandlersInitialized = true;
    }
    #savePlayerStatus() {
        console.log('profileStore.profile', profileStore.profile);
        if (!profileStore.profile?.settings.canSaveActiveTrackState)
            return;
        return httpClient.fetch('/api/v1/tracks/current/status', {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                currentPlaylistId: player.playlist?.id,
                currentTrackId: player.currentTrack?.id,
                currentTime: Math.round(player.currentTime),
                timestamp: new Date().toISOString(),
            })
        })
            .then(({ response }) => {
            if (!response.ok && response.status === 402) {
                profileStore.setIsPremium(false);
            }
        });
    }
    #startSendingTrackStatus() {
        console.log('start');
        this.#trackSendingIntervalId = setInterval(() => {
            this.#savePlayerStatus();
        }, 3000);
    }
    #stopSendingTrackStatus() {
        clearInterval(this.#trackSendingIntervalId);
        this.#trackSendingIntervalId = null;
        console.log('finish');
    }
    #updateMediaData() {
        if (!('mediaSession' in navigator) || !this.#currentTrack)
            return;
        const { title } = this.#currentTrack;
        navigator.mediaSession.metadata = new MediaMetadata({
            title,
        });
    }
}
export const player = new Player();
